/** Meta reports account_status as an enum int; these are the states an operator
 *  can actually encounter on a live buying account. */
const ACCOUNT_STATUS: Record<number, { label: string; tone: "ok" | "warn" | "err" }> = {
	1: { label: "Active", tone: "ok" },
	2: { label: "Disabled", tone: "err" },
	3: { label: "Unsettled", tone: "warn" },
	7: { label: "Risk review", tone: "warn" },
	8: { label: "Pending settlement", tone: "warn" },
	9: { label: "Grace period", tone: "warn" },
	100: { label: "Pending closure", tone: "err" },
	101: { label: "Closed", tone: "err" },
};

export function accountStatus(code: number): { label: string; tone: "ok" | "warn" | "err" } {
	return ACCOUNT_STATUS[code] ?? { label: "Unknown", tone: "warn" };
}

/**
 * How many minor units make one whole unit of this currency.
 *
 * Not always 100: JPY, KRW, VND and CLP have no minor unit at all, and KWD,
 * BHD, JOD and TND have three digits. Asking Intl keeps that list out of here
 * and current — `resolvedOptions()` reports the currency's own digits.
 */
function minorUnitsPerUnit(currency: string): number {
	try {
		const digits =
			new Intl.NumberFormat(undefined, { style: "currency", currency }).resolvedOptions()
				.maximumFractionDigits ?? 2;
		return 10 ** digits;
	} catch {
		return 100;
	}
}

/**
 * Meta reports spend_cap in the account currency's minor unit, and uses 0 to
 * mean "no cap set" — which in this system means no hard stop at all, since
 * fbads talks to the Graph API directly with no platform-side risk gate.
 *
 * The divisor follows the currency, never a flat 100. On a JPY account a cap of
 * ¥1,000,000 is reported as `1000000`, and dividing by 100 would render it as
 * ¥10,000 — the spend rail on the Overview screen is the one number this
 * console exists to state plainly, so it must not be off by two orders of
 * magnitude in either direction.
 */
export function spendCapFromMinor(
	minor: string | null | undefined,
	currency: string | null | undefined,
): { capped: boolean; unreadable?: boolean; text: string } {
	const raw = Number(minor ?? 0);

	/*
	 * Three answers, not two — "no cap", "this cap", and "we could not read it".
	 *
	 * `0` and null mean no cap, by agreement with Meta. Anything else that is not
	 * a positive finite number means our own mirror holds something we do not
	 * understand. Reporting that as "No limit set" states a fact about the
	 * account on the strength of a failure to read our own data.
	 *
	 * Unreadable is every shape of it, not just `NaN`. A negative cap and an
	 * infinity are values we *did* read and cannot make sense of, which is the
	 * same fault as one that will not parse — drawing the line at `NaN` alone
	 * would hold the rule for one spelling and drop it for another.
	 *
	 * The zero test is `=== 0` rather than `<= 0` on purpose: it makes the
	 * no-cap set explicit — null, undefined, "0", "" and whitespace all coerce
	 * to zero — instead of catching negatives on the way past.
	 *
	 * It stays `capped: false`, so the caution is unchanged and an unreadable
	 * account still raises the warning rather than passing quietly. What changes
	 * is what the operator is told. `APPROVING-TEAMS.md` makes checking the limit
	 * the control before an account is lent, so the flag's credibility is the
	 * safeguard: send somebody to Business Manager over a limit that turns out to
	 * exist, and next time they will not go.
	 */
	if (!Number.isFinite(raw) || raw < 0)
		return { capped: false, unreadable: true, text: "Limit unreadable" };
	if (raw === 0) return { capped: false, text: "No limit set" };
	// Without a currency we cannot know the minor unit; 100 is the common case
	// and the amount is unlabelled anyway.
	if (!currency) return { capped: true, text: String(Math.round(raw / 100)) };
	const amount = raw / minorUnitsPerUnit(currency);
	try {
		return {
			capped: true,
			text: new Intl.NumberFormat(undefined, {
				style: "currency",
				currency,
				maximumFractionDigits: 0,
			}).format(amount),
		};
	} catch {
		return { capped: true, text: `${Math.round(amount)} ${currency}` };
	}
}

/**
 * How many accounts sit in each of the three cap states.
 *
 * Lives here rather than inside `Overview.tsx` for a reason that is not tidiness.
 * The screen is `.tsx`, and `node --test --experimental-strip-types` refuses to
 * load that extension — `ERR_UNKNOWN_FILE_EXTENSION`, measured, not assumed. So
 * logic left in the component is logic nothing can execute, and this particular
 * logic is the difference between "no limit" and "we could not read it", which
 * is a claim about somebody's ad account made on a screen they lend from.
 *
 * The first three counts are disjoint and sum to the list length, which is what
 * makes them safe to render as separate sentences. `unreadable` is excluded from
 * `uncapped` deliberately: both raise a warning, but they send an operator to
 * different places, so counting one inside the other would make the banner say
 * something neither state means.
 *
 * `nearLimit` and `spendUnreadable` are **not** part of that partition — both
 * are subsets of `capped`, because only an account with a readable limit can be
 * near it or have its spend measured against it. Said here because the sentence
 * above used to describe every field this function returned, and a stated
 * invariant that quietly stops covering half the result is worse than none.
 * `format.test.ts` holds both halves: the three still partition, and the two are
 * still subsets.
 */
export function summariseCaps(
	accounts: Array<{
		spendCap: string | null;
		amountSpent: string | null;
		currency: string | null;
	}>,
): {
	capped: number;
	uncapped: number;
	unreadable: number;
	nearLimit: number;
	spendUnreadable: number;
} {
	let capped = 0;
	let uncapped = 0;
	let unreadable = 0;
	let nearLimit = 0;
	let spendUnreadable = 0;
	for (const a of accounts) {
		const r = spendCapFromMinor(a.spendCap, a.currency);
		if (r.unreadable) unreadable++;
		else if (r.capped) {
			capped++;
			// Asked of the same function the card renders from, so a card showing a
			// danger rail and a banner counting it can never disagree.
			const p = spendPosition(a.spendCap, a.amountSpent, a.currency);
			/*
			 * No bar means unread, and nothing more is needed to say so.
			 *
			 * This used to read `p.used === null && p.tone !== "ok"`, because the
			 * retired `absent` state produced exactly that combination — no bar, no
			 * warning — and counting it would have been a caution nobody earned.
			 * With `absent` gone there is no way for a capped row to reach here
			 * with no bar and an `ok` tone: every unreadable spend gives `warn`,
			 * checked across null, blank, corrupt, negative and infinite.
			 *
			 * So the condition became redundant rather than wrong, and restoring it
			 * still passes 123/0 — the two forms are identical in behaviour and no
			 * case can tell them apart. Said here so the next reader does not go
			 * looking for the case that would.
			 */
			if (p.used === null) spendUnreadable++;
			else if (p.tone === "danger") nearLimit++;
		} else uncapped++;
	}
	return { capped, uncapped, unreadable, nearLimit, spendUnreadable };
}

/** Prefer a human handle, but never render an empty string as the identity. */
export function displayName(me: { email?: string; id?: string } | null): string {
	const email = me?.email?.trim();
	if (email) return email;
	const id = me?.id?.trim();
	if (id) return id;
	return "your G1 account";
}

/**
 * How close an account is to the only hard stop this console has.
 *
 * A cap on its own is half a truth. `20000` with `4750` spent and `20000` with
 * `19900` spent are the same sentence on today's screen and are not the same
 * situation, and the second is the one an operator has to see without doing
 * arithmetic. This project rests its whole safety story on the Meta account
 * limit — `BUSINESS-PLAN.md` puts nothing else in the path of a spend — so a
 * limit shown without its consumption is a reassurance that has not been earned.
 *
 * Both numbers carry the unreadable state independently, and that is the point
 * rather than an edge case. `amount_spent` can be missing or unparseable for
 * exactly the reasons `spend_cap` can, and folding that into zero would say
 * "nothing spent" about an account whose spend we failed to read — the same
 * fault as reporting an unreadable cap as "no limit", arriving on the other
 * number. So an unreadable spend never draws a bar and never counts as low.
 */
export interface SpendPosition {
	/** What the left of the rail label says about the limit itself. */
	label: string;
	/** What the right says: the limit, and the spend when we have it. */
	text: string;
	/** Fill fraction 0–1, or `null` when a bar cannot be drawn honestly. */
	used: number | null;
	/**
	 * Draw the rail unterminated — no stop at its end.
	 *
	 * True when this console cannot point at a limit: none set, or one we could
	 * not read. Read by `summariseCaps` and by the cases; the screen gets `rail`
	 * instead, for the reason below.
	 */
	open: boolean;
	tone: "ok" | "warn" | "danger";
	/**
	 * The two things the card renders, finished.
	 *
	 * The first version returned `open`, `used` and `tone` and let the screen
	 * write the class names. The Reviewer measured what that still allowed:
	 *
	 *   className={spend.used === null ? "rail rail-open" : "rail"}
	 *
	 * — 113 pass, 0 fail. It draws a rail with **no stop** on an account whose
	 * limit is known and shown, because `used` is also null when the limit reads
	 * fine and the spend does not. Precisely what the rail exists not to do, and
	 * green.
	 *
	 * Guarding the screen was tried first and does not reach it: that expression
	 * imports nothing and does no arithmetic, so neither the import pin nor the
	 * arithmetic ban sees it. So the expression is gone rather than watched — the
	 * screen is handed strings and has nothing left to derive. Same move as the
	 * Overview banners, for the same reason and after the same measurement.
	 */
	rail: string;
	fill: { className: string; width: string } | null;
}

/**
 * The share of a cap at which an operator should be told without being asked.
 *
 * A judgement, not a fact, and named so that it reads as one. Too low and the
 * warning is background noise; too high and it arrives after delivery has
 * already stopped. Nothing downstream derives anything from the number — it is
 * used once, here.
 */
export const NEAR_LIMIT = 0.9;

export function spendPosition(
	minorCap: string | null | undefined,
	// Narrow on purpose: this is the parameter the expiry trip runs through, and
	// it was wide enough to swallow that trip once already.
	minorSpent: string | null,
	currency: string | null | undefined,
): SpendPosition {
	const cap = spendCapFromMinor(minorCap, currency);
	const spent = readMinor(minorSpent);
	const known = typeof spent === "number";

	// An unreadable spend is stated, never omitted: silence here reads as zero to
	// anybody looking at a bar that is not drawn.
	const spentText = known ? money(spent, currency) : "spend unreadable";

	if (cap.unreadable)
		// We cannot say how close this is to anything. Whatever we *did* read is
		// still worth saying — an unreadable cap over a large spend is a different
		// morning from an unreadable cap over none.
		return {
			label: "Cannot tell",
			text: known ? `${cap.text} · ${spentText} spent` : cap.text,
			used: null,
			open: true,
			tone: "danger",
			rail: "rail rail-open",
			fill: null,
		};

	if (!cap.capped)
		// No limit at all. The spend is the only number there is, and it is the
		// one that says how much this has already cost.
		return {
			label: "No hard stop",
			text: known ? `${spentText} spent` : cap.text,
			used: null,
			open: true,
			tone: "danger",
			rail: "rail rail-open",
			fill: null,
		};

	const capMinor = Number(minorCap);
	if (spent === "unreadable")
		return {
			label: "Spending limit",
			text: `${cap.text} · ${spentText}`,
			used: null,
			open: false,
			tone: "warn",
			rail: "rail",
			fill: null,
		};

	// Meta can report a spend at or slightly past the cap; clamped so the bar
	// stays a bar, while the tone below reads the unclamped share.
	const share = capMinor > 0 ? spent / capMinor : 0;
	const fraction = Math.max(0, Math.min(1, share));
	const hot = share >= NEAR_LIMIT;
	return {
		label: "Spending limit",
		text: `${money(spent, currency)} of ${money(capMinor, currency)}`,
		used: fraction,
		open: false,
		tone: hot ? "danger" : "ok",
		rail: "rail",
		fill: {
			className: hot ? "rail-fill rail-fill-hot" : "rail-fill",
			width: `${fraction * 100}%`,
		},
	};
}

/**
 * A spend figure, and the two answers it can give.
 *
 * There were three. While the worker did not send `amountSpent`, an absent
 * field meant "not deployed yet" and said nothing — a caution on every capped
 * account in the console over a number nobody had sent would have been a
 * caution nobody earned, which is what teaches an operator to skip the next
 * one. `#75` landed the worker half and the store sets the field on every row,
 * so that state has expired and is gone.
 *
 * `undefined` is still handled at runtime and now means what `null` means. The
 * type says it cannot arrive; the wire is not the type, and a response that has
 * stopped carrying a field it promises is a failure to read, not a zero.
 *
 * The expiry was written here with a mechanical trip attached — drop the `?`
 * from `amountSpent` in `api.ts` and the compiler forces this branch open.
 * **That trip did not work**, measured when it was pulled: `spendPosition`
 * accepted `string | null | undefined`, so narrowing the caller changed nothing
 * and typecheck stayed at 0. A trip is only a trip if the narrow type reaches
 * the function that branches on it, and this one stopped at the boundary. Both
 * signatures are narrowed now.
 *
 * A value that is present and unusable — null, corrupt, negative, infinite,
 * blank — is a real failure to read our own data and warns. Zero is an answer,
 * not an absence: nothing spent yet.
 */
type Spent = number | "unreadable";

function readMinor(minor: string | null): Spent {
	if (minor === null) return "unreadable";
	/*
	 * An empty or blank string is unreadable here, and it deliberately differs
	 * from the cap, where `""` coerces to zero and means "no limit set".
	 *
	 * That coercion is a decision written down in `spendCapFromMinor` and it is
	 * not being changed here — but it is not inherited by default either. On a
	 * cap, zero is a real value Meta uses to say "no limit". On a spend, zero
	 * means money that was not spent, and printing "$0.00 spent" about a field
	 * that arrived blank states a fact about somebody's account on the strength
	 * of an empty string. Whether the cap should treat `""` the same way is a
	 * fair question and a separate diff; it has a documented answer today and
	 * this one does not get to change it in passing.
	 */
	if (minor.trim() === "") return "unreadable";
	const raw = Number(minor);
	// Same rule as the cap, for the same reason: a negative or infinite figure is
	// one we read and cannot use, which is not different from one that will not
	// parse.
	return Number.isFinite(raw) && raw >= 0 ? raw : "unreadable";
}

/**
 * Minor units as money, falling back the same way the cap formatter does.
 *
 * The precision is the currency's own, not a number chosen here. Both halves of
 * "$47.50 of $200.00" go through this, so they agree by construction rather than
 * by two constants being kept in step — and a currency with no minor unit gets
 * none on either side. Forcing two digits was tried first and printed
 * "¥50,000.00 of ¥100,000.00", which is a decimal place JPY does not have.
 *
 * `spendCapFromMinor` rounds a standalone limit to whole units, which is right
 * for a figure on its own and wrong inside a comparison: "$47.50 of $200" puts
 * two precisions in one sentence and invites the reader to wonder which is
 * approximate. That function is untouched; this is only the comparison.
 *
 * The consequence, noticed by the Reviewer and kept on purpose: the same
 * account prints `$200 · spend unreadable` and `$47.50 of $200.00` depending on
 * whether the other number read. The rule is contextual rather than
 * accidental — a figure standing alone is rounded, a figure being compared is
 * exact — and making the standalone case exact too would change what every
 * card in the console shows today for the sake of a consistency nobody is
 * reading across.
 */
function money(minor: number, currency: string | null | undefined): string {
	if (!currency) return String(Math.round(minor / 100));
	const unit = minorUnitsPerUnit(currency);
	const amount = minor / unit;
	try {
		return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
	} catch {
		return `${amount.toFixed(Math.log10(unit))} ${currency}`;
	}
}
